import React, { useMemo } from 'react';
import { BsChatSquareText, BsChevronDown, BsPencil, BsPlus, BsTrash } from 'react-icons/bs';
import {
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DatePicker from '../../../../../../../../../../../../../../components/DatePicker';
import DropdownMenu from '../../../../../../../../../../../../../../components/DropdownMenu';
import colors from '../../../../../../../../../../../../../../styles/theme/foundation/colors';

import { createRow, policyTypeLabel } from './util';

const ROW_COLUMNS = 'minmax(0, 1fr) 200px auto';
const ROW_HEIGHT = '48px';
const NO_POLICY_TYPE = { id: null, displayName: 'No policy type' };

/**
 * Editable list of expiration line items. Purely presentational: the caller owns `rows` and
 * persists them however it likes.
 */
const ExpirationRowsEditor = ({
  rows,
  setRows,
  policyTypes,
  heading = 'Dates'
}) => {
  const policyTypeOptions = useMemo(() => [
    NO_POLICY_TYPE,
    ...policyTypes
      .filter(type => !type.deletedAt)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
  ], [policyTypes]);

  const updateRow = (key, changes) => {
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...changes } : row)));
  };

  const removeRow = key => {
    setRows(prev => prev.filter(row => row.key !== key));
  };

  const addRow = () => {
    setRows(prev => [...prev, createRow({ isExpanded: true })]);
  };

  const setRowPolicyType = (key, policyTypeId) => {
    setRows(prev => prev.map(row => (row.key === key
      ? { ...row, policyTypeId, label: policyTypeId ? '' : row.label }
      : row)));
  };

  return (
    <Box className="ExpirationRowsEditor" textAlign="left">
      <Flex alignItems="center" justifyContent="space-between">
        <Text color={colors.greyScale['900']} fontSize="18px" fontWeight="semibold">
          {heading}
        </Text>
      </Flex>

      <Grid
        alignItems="center"
        borderBottom="1px solid"
        borderColor={colors.greyScale['200']}
        color={colors.greyScale['700']}
        fontSize="16px"
        mt={4}
        pb={3}
        templateColumns={ROW_COLUMNS}
      >
        <Text>Label</Text>
        <Text pl={8}>Expiration date</Text>
        <Button
          borderStyle="dashed"
          leftIcon={<Icon as={BsPlus} boxSize="20px" />}
          size="md"
          variant="secondary"
          onClick={addRow}
        >
          Add Date
        </Button>
      </Grid>

      {rows.map(row => {
        const typeLabel = policyTypeLabel(policyTypes, row.policyTypeId);
        const hasNote = row.note.trim().length > 0;

        return (
          <Box borderBottom="1px solid" borderColor={colors.greyScale['200']} key={row.key} py={4}>
            <Grid alignItems="start" gap={3} templateColumns={ROW_COLUMNS}>
              {typeLabel ? (
                <Flex alignItems="center" h={ROW_HEIGHT} minW={0}>
                  <Box
                    as="span"
                    bg={colors.primaryScale['50']}
                    borderRadius="6px"
                    color={colors.primaryScale['700']}
                    display="inline-block"
                    fontSize="14px"
                    fontWeight="500"
                    maxW="100%"
                    overflow="hidden"
                    px={3}
                    py={1}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {typeLabel}
                  </Box>
                </Flex>
              ) : (
                <InputGroup>
                  <Input
                    placeholder="Label"
                    size="lg"
                    value={row.label}
                    variant="primary"
                    onChange={e => updateRow(row.key, { label: e.target.value })}
                  />
                  <InputRightElement h="100%" pointerEvents="none">
                    <Icon as={BsPencil} boxSize="16px" color={colors.primaryScale['600']} />
                  </InputRightElement>
                </InputGroup>
              )}

              <DatePicker
                dateFormat="MM/DD/YYYY"
                existingDate={row.date}
                placeholderText="MM/DD/YYYY"
                size="lg"
                width="100%"
                onDateSelect={date => updateRow(row.key, { date: date || null })}
              />

              <HStack h={ROW_HEIGHT} spacing={0}>
                <Tooltip hasArrow label={row?.note || '–'} placement="top">
                  <Box as="span" color={colors.greyScale['600']} display="inline-flex" px={2}>
                    <Icon as={BsChatSquareText} boxSize="16px" />
                  </Box>
                </Tooltip>

                <IconButton
                  aria-label={row.isExpanded ? 'Hide details' : 'Show details'}
                  icon={(
                    <Icon
                      as={BsChevronDown}
                      boxSize="16px"
                      transform={row.isExpanded ? 'rotate(180deg)' : undefined}
                      transition="transform 0.2s"
                    />
                  )}
                  size="sm"
                  variant="textGrey"
                  onClick={() => updateRow(row.key, { isExpanded: !row.isExpanded })}
                />

                <IconButton
                  aria-label="Remove date"
                  icon={<Icon as={BsTrash} boxSize="18px" />}
                  size="sm"
                  variant="textGrey"
                  onClick={() => removeRow(row.key)}
                />
              </HStack>
            </Grid>

            <Collapse animateOpacity in={row.isExpanded}>
              <Grid gap={3} mt={3} pr="88px" templateColumns="minmax(0, 1fr) minmax(0, 1fr)">
                <DropdownMenu
                  isSearchable
                  useOriginal
                  labelKey="displayName"
                  options={policyTypeOptions}
                  placeholder={NO_POLICY_TYPE.displayName}
                  selected={typeLabel || undefined}
                  width="100%"
                  onSelect={type => setRowPolicyType(row.key, type.id)}
                />

                <Input
                  placeholder="Note, e.g. where the date came from"
                  size="md"
                  value={row.note}
                  onChange={e => updateRow(row.key, { note: e.target.value })}
                />
              </Grid>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

ExpirationRowsEditor.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    id: PropTypes.number,
    label: PropTypes.string.isRequired,
    date: PropTypes.instanceOf(Date),
    policyTypeId: PropTypes.number,
    note: PropTypes.string.isRequired,
    isExpanded: PropTypes.bool.isRequired
  })).isRequired,
  setRows: PropTypes.func.isRequired,
  policyTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    displayName: PropTypes.string,
    deletedAt: PropTypes.string
  })).isRequired,
  heading: PropTypes.string
};

export default ExpirationRowsEditor;
